import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { costingImportsAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { WeightChangeAlert } from './WeightChangeAlert';
import { toast } from 'sonner';

const emptyComponent = () => ({ name: '', category: 'other', basis: 'per_piece', rate: 0, amount_per_piece: 0, notes: '' });

export const CostingImportReviewDialog = ({ record, open, onOpenChange, onRefresh }) => {
  const navigate = useNavigate();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) setDraft({ ...record.draft_data, cost_components: [...(record.draft_data?.cost_components || [])] });
  }, [record]);

  if (!record) return null;

  const setField = (field, value) => setDraft((current) => ({ ...current, [field]: value }));
  const updateComponent = (index, field, value) => setDraft((current) => ({
    ...current,
    cost_components: current.cost_components.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item),
  }));
  const removeComponent = (index) => setDraft((current) => ({
    ...current, cost_components: current.cost_components.filter((_, itemIndex) => itemIndex !== index),
  }));

  const save = async () => {
    setSaving(true);
    try {
      await costingImportsAPI.update(record.id, { inquiry_product_id: record.inquiry_product_id, draft_data: draft });
      toast.success('Costing review saved');
      await onRefresh();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not save costing review');
      return false;
    } finally { setSaving(false); }
  };

  const confirm = async () => {
    if (!(await save())) return;
    setSaving(true);
    try {
      const response = await costingImportsAPI.confirm(record.id);
      toast.success(`${response.data.costing_number} confirmed and activated`);
      onOpenChange(false);
      await onRefresh();
      navigate(`/costings/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not confirm costing');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] max-w-5xl flex-col overflow-hidden" data-testid="costing-import-review-dialog">
        <DialogHeader>
          <DialogTitle>Review extracted costing</DialogTitle>
          <DialogDescription>{record.filename} remains retained on this Inquiry. Nothing becomes active until confirmation.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 space-y-6 overflow-y-auto pr-2">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[['currency', 'Currency'], ['quantity', 'Quantity'], ['quantity_unit', 'Unit'], ['net_weight_grams', 'Net weight (g)']].map(([field, label]) => (
              <div className="space-y-2" key={field}><Label>{label}</Label><Input value={draft[field] ?? ''} type={['quantity', 'net_weight_grams'].includes(field) ? 'number' : 'text'} onChange={(event) => setField(field, event.target.value)} data-testid={`costing-import-${field}-input`} /></div>
            ))}
          </div>
          <WeightChangeAlert
            alert={record.weight_alert}
            sheetWeight={draft.net_weight_grams}
            testId="costing-import-weight-alert"
          />
          <div className="border-t pt-5">
            <div className="mb-3 flex items-center justify-between"><div><h3 className="font-heading font-bold">Cost components</h3><p className="text-sm text-muted-foreground">Verify every extracted row against the retained source.</p></div>
              <Button type="button" variant="outline" size="sm" onClick={() => setDraft((current) => ({ ...current, cost_components: [...(current.cost_components || []), emptyComponent()] }))} data-testid="add-cost-component-button"><Plus className="mr-2 h-4 w-4" />Add row</Button></div>
            <div className="space-y-3" data-testid="costing-import-components-list">{(draft.cost_components || []).map((item, index) => (
              <div className="grid gap-3 border p-3 lg:grid-cols-[1.3fr_1fr_1fr_1fr_1fr_auto]" key={item.id || index} data-testid={`costing-import-component-${index}`}>
                <Input value={item.name || ''} placeholder="Component" onChange={(event) => updateComponent(index, 'name', event.target.value)} data-testid={`costing-component-name-${index}`} />
                <select className="h-10 border bg-white px-3 text-sm" value={item.category || 'other'} onChange={(event) => updateComponent(index, 'category', event.target.value)} data-testid={`costing-component-category-${index}`}>
                  {['raw_material', 'process', 'tooling', 'quality', 'packaging', 'overhead', 'finance', 'other'].map((option) => <option value={option} key={option}>{option.replaceAll('_', ' ')}</option>)}
                </select>
                <select className="h-10 border bg-white px-3 text-sm" value={item.basis || 'other'} onChange={(event) => updateComponent(index, 'basis', event.target.value)} data-testid={`costing-component-basis-${index}`}>
                  {['per_piece', 'per_kg', 'fixed', 'percentage', 'other'].map((option) => <option value={option} key={option}>{option.replaceAll('_', ' ')}</option>)}
                </select>
                <Input type="number" step="0.0001" value={item.rate ?? 0} onChange={(event) => updateComponent(index, 'rate', event.target.value)} data-testid={`costing-component-rate-${index}`} />
                <Input type="number" step="0.0001" value={item.amount_per_piece ?? 0} onChange={(event) => updateComponent(index, 'amount_per_piece', event.target.value)} data-testid={`costing-component-amount-${index}`} />
                <Button size="icon" variant="ghost" onClick={() => removeComponent(index)} title="Remove cost row" data-testid={`remove-cost-component-${index}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
              </div>
            ))}</div>
          </div>
          <div className="grid gap-4 border-t pt-5 sm:grid-cols-3">
            {[['total_cost_per_piece', 'Total cost / piece'], ['margin_percentage', 'Margin %'], ['selling_price_per_piece', 'Selling price / piece']].map(([field, label]) => (
              <div className="space-y-2" key={field}><Label>{label}</Label><Input type="number" step="0.0001" value={draft[field] ?? 0} onChange={(event) => setField(field, event.target.value)} data-testid={`costing-import-${field}-input`} /></div>
            ))}
          </div>
          <div className="space-y-2"><Label>Review notes</Label><Textarea value={draft.notes || ''} onChange={(event) => setField('notes', event.target.value)} data-testid="costing-import-notes-input" /></div>
        </div>
        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-costing-review-button">Close</Button>
          <Button variant="outline" onClick={save} disabled={saving} data-testid="save-costing-review-button">Save review</Button>
          <Button onClick={confirm} disabled={saving} data-testid="confirm-costing-import-button">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Confirm & activate costing</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};